// Holds the build the one reload was spent on, for the life of the tab.
const RELOADED_BUILD_KEY = 'recruiter-ui:chunk-reload-build';

const reloadedErrors = new WeakSet<object>();
let pendingChunkErrorHandler: ((event: Event) => void) | undefined;

export const setPendingChunkErrorHandler = (
  handler?: (event: Event) => void,
): void => {
  pendingChunkErrorHandler = handler;
};

/** Whether a reload already answers this error. */
export const isReloadedChunkError = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && reloadedErrors.has(error);

const spendReload = (build: string): boolean => {
  try {
    if (window.sessionStorage.getItem(RELOADED_BUILD_KEY) === build) {
      return false;
    }
    window.sessionStorage.setItem(RELOADED_BUILD_KEY, build);
    return true;
  } catch {
    // Without storage nothing would stop a reload loop.
    return false;
  }
};

/**
 * Reloads the page once when a lazily loaded chunk fails to load.
 *
 * Vite dispatches `vite:preloadError` on window for every dynamic import that
 * fails, with the error as `payload`. A route chunk that fails otherwise
 * replaces the page with the error boundary's screen, which asks the recruiter
 * to reload, and a reload fetches it again. One reload per build per tab: a chunk that fails
 * again after it, or a second failure on the same build, still throws and
 * reports. A pending chunk handler can prevent the event before the reload is spent.
 */
export function reloadOnChunkLoadError(build: string): void {
  window.addEventListener('vite:preloadError', event => {
    if (!event.defaultPrevented) pendingChunkErrorHandler?.(event);
    if (event.defaultPrevented || !spendReload(build)) return;

    const { payload } = event as Event & { payload?: unknown };
    if (typeof payload === 'object' && payload !== null) {
      reloadedErrors.add(payload);
    }
    window.location.reload();
  });
}
